import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import { RLink } from "./RLink";
import Tooltip from "./Tooltip";

export type AvatarStackUser = {
  username: string;
  avatar_url: string;
};

interface AvatarStackProps {
  users: AvatarStackUser[];
  anonymous?: boolean;
  size?: "small" | "medium" | "large";
  showPlayButton?: boolean;
  onPlayClick?: () => void;
  /** When true, clicking opens a popover for multiple users or navigates for single user */
  linkToProfiles?: boolean;
}

export const AvatarStack = ({
  users,
  anonymous,
  size = "medium",
  showPlayButton = false,
  onPlayClick,
  linkToProfiles = false,
}: AvatarStackProps): JSX.Element => {
  const history = useHistory();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const avatarRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const sizeClasses = {
    small: "h-[40px] w-[40px]",
    medium: "h-[50px] w-[50px]",
    large: "h-[80px] w-[80px]",
  };

  const sizeClass = sizeClasses[size];

  // Close popover when clicking outside
  useEffect(() => {
    if (!popoverOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        avatarRef.current &&
        !avatarRef.current.contains(target)
      ) {
        setPopoverOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [popoverOpen]);

  // Update popover position when opened
  useEffect(() => {
    if (popoverOpen && avatarRef.current) {
      const rect = avatarRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  }, [popoverOpen]);

  const handleAvatarClick = (e: React.MouseEvent) => {
    if (!linkToProfiles) return;

    e.preventDefault();
    e.stopPropagation();

    if (users.length === 1 && users[0].username) {
      history.push(`/user/${users[0].username}`);
    } else if (users.length > 1) {
      setPopoverOpen((prev) => !prev);
    }
  };

  // If anonymous or no users, show anonymous chip
  if (anonymous || users.length === 0) {
    return (
      <div className={`group relative flex-shrink-0 ${sizeClass}`}>
        <img
          className={`${sizeClass} rounded object-cover`}
          src="/img/anonymous-chip.png"
          alt="Anonymous"
        />
      </div>
    );
  }

  const primaryUser = users[0];
  const additionalCount = users.length - 1;
  const allUsernames = users.map((u) => u.username).join(", ");

  const isClickable = linkToProfiles && users.some((u) => u.username);
  const cursorClass = isClickable ? "cursor-pointer" : "";

  const popoverContent =
    popoverOpen && users.length > 1
      ? createPortal(
          <div
            ref={popoverRef}
            className="fixed z-[9999] min-w-[160px] rounded-lg border border-slate-200 bg-white py-2 shadow-xl dark:border-white/10 dark:bg-slate-900"
            style={{
              top: popoverPosition.top - 8,
              left: popoverPosition.left,
              transform: "translateY(-100%)",
            }}
          >
            {users
              .filter((u) => u.username)
              .map((user) => (
                <RLink
                  key={user.username}
                  to={`/user/${user.username}`}
                  className="flex items-center gap-3 px-4 py-2.5 text-base text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                  onClick={() => setPopoverOpen(false)}
                >
                  <img
                    src={user.avatar_url}
                    alt={user.username}
                    className="h-7 w-7 rounded object-cover"
                  />
                  {user.username}
                </RLink>
              ))}
          </div>,
          document.body
        )
      : null;

  const avatarContent = (
    <div
      ref={avatarRef}
      className={`group relative flex-shrink-0 ${sizeClass} ${cursorClass}`}
      onClick={isClickable ? handleAvatarClick : undefined}
    >
      <img
        className={`${sizeClass} rounded object-cover`}
        src={primaryUser.avatar_url}
        alt={primaryUser.username}
      />
      {additionalCount > 0 && (
        <div className="absolute bottom-0 right-0 rounded-tl bg-black/70 px-1 text-xs font-medium text-white">
          +{additionalCount}
        </div>
      )}
      {showPlayButton && onPlayClick && (
        <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onPlayClick();
            }}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md dark:bg-slate-800"
          >
            <i
              className="fa fa-play text-xs text-chips-gold"
              aria-hidden="true"
            ></i>
          </button>
        </div>
      )}
      {popoverContent}
    </div>
  );

  // If linkToProfiles is enabled, we handle navigation via click
  if (linkToProfiles) {
    // For multiple users, show tooltip on hover (popover on click)
    if (users.length > 1) {
      return (
        <Tooltip
          element={avatarContent}
          tooltip={<>{allUsernames}</>}
          position="top"
        />
      );
    }
    // For single user, just return the clickable avatar
    return avatarContent;
  }

  // Original behavior: tooltip for multiple users, link wrapper for single user
  if (users.length > 1) {
    return (
      <Tooltip
        element={avatarContent}
        tooltip={<>{allUsernames}</>}
        position="top"
      />
    );
  }

  // Single user, link to their profile
  return <RLink to={`/user/${primaryUser.username}`}>{avatarContent}</RLink>;
};
